import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";

import MessagePreviewScreen from "../../../shared/components/common/ui/MessagePreviewScreen/MessagePreviewScreen";

import { useTemplateConfig } from "../hooks/useTemplateConfig";
import { useChannelsData } from "../../channels/hooks/useChannelsData";
import { formatPhone } from "../utils/formatPhone";
import { UploadTemplateMedia } from "../../../services/api/templates/UploadTemplateMedia";

import CategoriesSelector from "../components/CategoriesSelector/CategoriesSelector";
import TextSection from "../components/TextSection/TextSection";
import HeaderPage from "../../../shared/components/HeaderPage/HeaderPage";
import { analyzeVariableText } from "../utils/variableTokens";

const uniformTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: 40,
  },
  "& .MuiOutlinedInput-input": {
    padding: "8.5px 14px",
  },
  "& .MuiSelect-select": {
    padding: "8.5px 14px",
    display: "flex",
    alignItems: "center",
  },
};

const ADD_CHANNEL_VALUE = "__add_channel__";
const EMAIL_ACCENT_COLORS = [
  "#FFFFFF",
  "#C80815",
  "#D6A03A",
  "#77B255",
  "#2A47CC",
  "#D71CB6",
  "#7928E8",
];

export default function TemplatePageChannelAdd({ channel }) {
  const navigate = useNavigate();

  const config = useTemplateConfig();
  const { channels, loading } = useChannelsData();

  const [category, setCategory] = useState("Geral");
  const [selectedChannel, setSelectedChannel] = useState("");
  const [name, setName] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailSubtitle, setEmailSubtitle] = useState("");
  const [emailAccentColor, setEmailAccentColor] = useState("#7928E8");
  const [saveValidationNotice, setSaveValidationNotice] = useState(null);
  const [mediaTargetMessageId, setMediaTargetMessageId] = useState(null);
  const [pendingMediaKind, setPendingMediaKind] = useState(null);
  const [mediaModeDialogOpen, setMediaModeDialogOpen] = useState(false);
  const imageInputRef = useRef(null);
  const pdfInputRef = useRef(null);

  const filteredChannels = channels.filter((ch) => ch.type === channel);
  const variableValidation = useMemo(() => {
    const outgoingMessages = config.messages.filter((m) => m.fromMe);

    const acc = outgoingMessages.reduce(
      (result, message) => {
        const analysis = analyzeVariableText(message.content || "");

        result.hasInvalidVariables = result.hasInvalidVariables || analysis.hasInvalidVariables;
        result.hasWarningTokens = result.hasWarningTokens || analysis.hasWarningTokens;
        result.startsWithVariable = result.startsWithVariable || analysis.startsWithVariable;
        result.endsWithVariable = result.endsWithVariable || analysis.endsWithVariable;

        analysis.invalidVariableTokens.forEach((token) => result.invalidTokens.add(token));
        analysis.warningTokens.forEach((token) => result.warningTokens.add(token));

        return result;
      },
      {
        hasInvalidVariables: false,
        hasWarningTokens: false,
        startsWithVariable: false,
        endsWithVariable: false,
        invalidTokens: new Set(),
        warningTokens: new Set(),
      },
    );

    return {
      hasInvalidVariables: acc.hasInvalidVariables,
      hasWarningTokens: acc.hasWarningTokens,
      startsWithVariable: acc.startsWithVariable,
      endsWithVariable: acc.endsWithVariable,
      invalidTokens: Array.from(acc.invalidTokens),
      warningTokens: Array.from(acc.warningTokens),
      hasHardBlock:
        acc.hasInvalidVariables || acc.startsWithVariable || acc.endsWithVariable,
    };
  }, [config.messages]);

  // garante que o valor selecionado sempre exista no select
  useEffect(() => {
    if (
      selectedChannel &&
      !filteredChannels.some((ch) => ch.id === selectedChannel)
    ) {
      setSelectedChannel("");
    }
  }, [filteredChannels, selectedChannel]);

  const handleChannelChange = (e) => {
    const value = e.target.value;

    if (value === ADD_CHANNEL_VALUE) {
      navigate("/canais/adicionar");
      return;
    }

    setSelectedChannel(value);
  };

  const handleSave = async () => {
    setSaveValidationNotice(null);

    if (!name) {
      alert("Defina um nome antes de salvar.");
      return;
    }

    if (!selectedChannel) {
      alert("Selecione um canal antes de salvar.");
      return;
    }

    if (channel === "email" && !emailSubject.trim()) {
      alert("Defina o assunto do e-mail antes de salvar.");
      return;
    }

    if (variableValidation.hasInvalidVariables) {
      const invalidList = variableValidation.invalidTokens.slice(0, 4).join(", ");
      const suffix =
        variableValidation.invalidTokens.length > 4
          ? " (e outras variáveis inválidas)"
          : "";
      setSaveValidationNotice({
        severity: "error",
        message: `Existem variáveis inválidas no texto: ${invalidList}${suffix}. Corrija antes de salvar.`,
      });
      return;
    }

    if (variableValidation.startsWithVariable || variableValidation.endsWithVariable) {
      if (variableValidation.startsWithVariable && variableValidation.endsWithVariable) {
        setSaveValidationNotice({
          severity: "warning",
          message: "O modelo não pode iniciar nem finalizar com variável.",
        });
        return;
      }

      if (variableValidation.startsWithVariable) {
        setSaveValidationNotice({
          severity: "warning",
          message: "O modelo não pode iniciar com variável.",
        });
        return;
      }

      if (variableValidation.endsWithVariable) {
        setSaveValidationNotice({
          severity: "warning",
          message: "O modelo não pode finalizar com variável.",
        });
      }
      return;
    }

    if (variableValidation.hasWarningTokens) {
      setSaveValidationNotice({
        severity: "caution",
        message:
          "Detectamos possíveis variáveis formatadas incorretamente. Consulte nossa listagem de variáveis disponíveis em \"+ Adicionar Variável\" ou confirme a ação para tentar salvar mesmo assim.",
      });
      const shouldContinue = window.confirm(
        "Detectamos possíveis variáveis formatadas incorretamente. Consulte nossa listagem de variáveis disponíveis em \"+ Adicionar Variável\" ou confirme a ação para tentar salvar mesmo assim.",
      );
      if (!shouldContinue) return;
    }

    const success = await config.save(
      selectedChannel,
      channel,
      category,
      name,
      emailSubject,
      emailSubtitle,
      emailAccentColor,
    );

    if (success) {
      navigate("/modelos", {
        state: { shouldRefetch: true },
      });
    }
  };

  const handlePickImage = (messageId) => {
    if (channel === "email") {
      setMediaTargetMessageId(messageId || "auto-reply");
      imageInputRef.current?.click();
      return;
    }

    if (channel !== "whatsapp_api") return;
    setMediaTargetMessageId(messageId);
    setPendingMediaKind("image");
    setMediaModeDialogOpen(true);
  };

  const handlePickPdf = (messageId) => {
    if (channel !== "whatsapp_api") return;
    setMediaTargetMessageId(messageId);
    setPendingMediaKind("pdf");
    setMediaModeDialogOpen(true);
  };

  const handleCloseMediaModeDialog = () => {
    setMediaModeDialogOpen(false);
    setPendingMediaKind(null);
  };

  const handleSelectDynamicMedia = () => {
    const messageId = mediaTargetMessageId;
    const kind = pendingMediaKind;
    if (!messageId || !kind) return;

    const headerType = kind === "pdf" ? "pdf" : "img";
    const dynamicValue = kind === "pdf" ? "{{Link_PDF}}" : "{{Link_Imagem}}";

    config.setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              header: {
                ...(m.header || {}),
                type: headerType,
                content: dynamicValue,
                mode: "dynamic",
                name: "",
              },
            }
          : m,
      ),
    );

    handleCloseMediaModeDialog();
    setMediaTargetMessageId(null);
  };

  const handleSelectSingleFileMedia = () => {
    if (!pendingMediaKind) return;
    if (pendingMediaKind === "pdf") {
      pdfInputRef.current?.click();
    } else {
      imageInputRef.current?.click();
    }
    setMediaModeDialogOpen(false);
  };

  const handleFileSelected = async (kind, event) => {
    const file = event.target.files?.[0];
    const messageId = mediaTargetMessageId;
    event.target.value = "";

    if (!file || !messageId) return;

    const headerType = kind === "image" ? "img" : "pdf";

    config.setMessages((prev) =>
      prev.map((m) =>
        m.id === messageId
          ? {
              ...m,
              header: {
                ...(m.header || {}),
                type: headerType,
                content: "",
                mode: "single_file",
                name: file.name,
                uploading: true,
              },
            }
          : m,
      ),
    );

    try {
      const publicUrl = await UploadTemplateMedia(file);

      config.setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                header: {
                  ...(m.header || {}),
                  type: headerType,
                  content: publicUrl,
                  mode: "single_file",
                  name: file.name,
                  uploading: false,
                },
              }
            : m,
        ),
      );
    } catch (error) {
      console.error("Erro ao subir mídia do template:", error);

      config.setMessages((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const { header, ...rest } = m;
          return rest;
        }),
      );

      alert("Não foi possível enviar o arquivo. Tente novamente.");
    }

    setMediaTargetMessageId(null);
    setPendingMediaKind(null);
  };

  const handleReplaceMedia = (messageId, mediaKind) => {
    const normalizedKind = String(mediaKind || "").toLowerCase();
    if (normalizedKind === "pdf") {
      handlePickPdf(messageId);
      return;
    }
    handlePickImage(messageId);
  };

  const handleRemoveMedia = (messageId) => {
    config.setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== messageId) return m;
        const { header, ...rest } = m;
        return rest;
      }),
    );
  };

  const variableToken = pendingMediaKind === "pdf" ? "{{Link_PDF}}" : "{{Link_Imagem}}";
  const mediaLabel = pendingMediaKind === "pdf" ? "PDF" : "Imagem";

  return (
    <>
      <HeaderPage />
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={(e) => handleFileSelected("image", e)}
      />
      <input
        ref={pdfInputRef}
        type="file"
        accept="application/pdf,.pdf"
        style={{ display: "none" }}
        onChange={(e) => handleFileSelected("pdf", e)}
      />
      <Box sx={{ width: "100%", p: "40px", backgroundColor: "#f5f6fa" }}>
        <Dialog
          open={mediaModeDialogOpen}
          onClose={handleCloseMediaModeDialog}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle sx={{ pb: 1 }}>Como enviar este {mediaLabel}?</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: 14, color: "text.secondary", mb: 2 }}>
              Escolha uma opção de envio para esta mídia.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                  <LinkOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Envio dinâmico</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 0.8 }}>
                  Cada destinatário recebe um link variável diferente.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "text.secondary",
                    mb: 0.8,
                    lineHeight: 1.35,
                  }}
                >
                  Importante: no envio dinâmico, essa variável precisa existir e estar
                  mapeada na listagem usada como base no momento do envio.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: "monospace",
                    backgroundColor: "#eef2f7",
                    borderRadius: 1,
                    px: 1.2,
                    py: 0.8,
                    mb: 1.2,
                    display: "inline-block",
                  }}
                >
                  Variável usada: {variableToken}
                </Typography>
                <Box>
                  <Button
                    variant="contained"
                    onClick={handleSelectDynamicMedia}
                    sx={{ textTransform: "none" }}
                  >
                    Usar envio dinâmico
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 1.5,
                  backgroundColor: "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.8 }}>
                  <UploadFileOutlinedIcon sx={{ color: "primary.main", fontSize: 20 }} />
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Arquivo único</Typography>
                </Box>
                <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 1.2 }}>
                  Você seleciona um arquivo único para ser usado no envio.
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: "monospace",
                    backgroundColor: "#eef2f7",
                    borderRadius: 1,
                    px: 1.2,
                    py: 0.8,
                    mb: 1.2,
                    display: "block",
                    width: "fit-content",
                  }}
                >
                  1 arquivo fixo para toda a campanha
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleSelectSingleFileMedia}
                  sx={{ textTransform: "none" }}
                >
                  Selecionar arquivo
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseMediaModeDialog} sx={{ textTransform: "none" }}>
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>

        <Grid container spacing={4}>
          {/* FORM */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {/* Nome do modelo + Canal */}
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nome do modelo"
                      placeholder="Ex: Boas-vindas"
                      sx={uniformTextFieldSx}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Canal"
                      sx={uniformTextFieldSx}
                      value={selectedChannel}
                      onChange={handleChannelChange}
                      disabled={loading}
                      SelectProps={{
                        renderValue: (selected) => {
                          const ch = filteredChannels.find(
                            (x) => x.id === selected,
                          );
                          if (!ch) return "";

                          // 👇 no selecionado: só 1 linha (mais comum)
                          return (
                            <Box
                              sx={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                fontWeight: 500,
                              }}
                            >
                              {ch.instance_name}
                            </Box>
                          );
                        },
                      }}
                    >
                      {filteredChannels.length > 0 ? (
                        filteredChannels.map((ch) => (
                          <MenuItem key={ch.id} value={ch.id}>
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0,
                              }}
                            >
                              <Box
                                sx={{
                                  fontWeight: 500,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {ch.instance_name}
                              </Box>

                              <Box
                                sx={{
                                  color: "text.secondary",
                                  fontSize: 13,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatPhone(ch.sender)}
                              </Box>
                            </Box>
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem
                          value={ADD_CHANNEL_VALUE}
                          sx={{
                            color: "primary.main",
                            fontWeight: 500,
                          }}
                        >
                          Adicionar canal
                        </MenuItem>
                      )}
                    </TextField>
                  </Grid>
                </Grid>
              </Grid>

              {/* Categoria */}
              {channel === "email" && (
                <>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Assunto do e-mail"
                      placeholder="Ex: Confirmação de cadastro"
                      sx={uniformTextFieldSx}
                      value={emailSubject}
                      onChange={(e) => setEmailSubject(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Subtítulo do e-mail"
                      placeholder="Ex: Modelo aprovado para envio"
                      sx={uniformTextFieldSx}
                      value={emailSubtitle}
                      onChange={(e) => setEmailSubtitle(e.target.value)}
                    />
                  </Grid>
                </>
              )}

              {/* Categoria */}
              <Grid item xs={12}>
                <CategoriesSelector value={category} onChange={setCategory} />
              </Grid>

              {/* Texto */}
              <Grid item xs={12}>
                <TextSection
                  channel={channel}
                  messages={config.messages}
                  setMessages={config.setMessages}
                />
              </Grid>

              {/* Salvar */}
              <Grid item xs={12}>
                {!!saveValidationNotice && (
                  <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.8 }}>
                    <HelpOutlineIcon
                      sx={{
                        fontSize: 18,
                        color:
                          saveValidationNotice.severity === "error"
                            ? "error.main"
                            : saveValidationNotice.severity === "warning"
                              ? "#D97706"
                              : "#CA8A04",
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: "text.secondary", fontWeight: 500 }}>
                      {saveValidationNotice.message}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={config.saving}
                    startIcon={
                      config.saving ? <CircularProgress size={18} /> : null
                    }
                  >
                    {config.saving ? "Salvando..." : "Adicionar Modelo"}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* PREVIEW */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                backgroundColor: "background.paper",
                borderRadius: 2,
                p: 3,
                boxShadow: 1,
                height: "100%",
              }}
            >
              <MessagePreviewScreen
                channel={channel}
                messages={config.messages}
                emailSubject={emailSubject}
                emailSubtitle={emailSubtitle}
                emailAccentColor={emailAccentColor}
                emailAccentColors={EMAIL_ACCENT_COLORS}
                onChangeEmailAccentColor={setEmailAccentColor}
                onChangeMessageButtons={(id, buttons) =>
                  config.setMessages((prev) =>
                    prev.map((m) => (m.id === id ? { ...m, buttons } : m)),
                  )
                }
                onPickImage={handlePickImage}
                onPickPdf={handlePickPdf}
                onReplaceMedia={handleReplaceMedia}
                onRemoveMedia={handleRemoveMedia}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}
